import { useEffect, useMemo, useState } from "react";
import { api, ApiError, DemoGrantRow, DemoIssueResult, DownloadLink, LicenseRow } from "../api/client";
import { useToast } from "../ui/toast";
import { IconCopy, IconPlus, IconRefresh, IconSearch, IconX } from "../ui/icons";
import { DataTable, type Column } from "../ui/DataTable";
import { fmtDate, visualStatus } from "../ui/licenseStatus";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_OR_DOMAIN_RE = /^([^\s@]+@)?[^\s@]+\.[^\s@]+$/;

/**
 * DEMO licences: issue a 7-day, resource-capped (1 site + 3 OneDrive) key to an email,
 * see the anti-abuse ledger (1 per company DOMAIN + 1 per tenant; free providers fall back to
 * 1 per email), and free a domain so the customer can request a fresh demo via the portal.
 * "Ważna do" + "Status" are joined from the licence list by licenseId (same as the Licencje card).
 */
export function Demo() {
  const toast = useToast();
  const [rows, setRows] = useState<DemoGrantRow[] | null>(null);
  const [licMap, setLicMap] = useState<Record<string, LicenseRow>>({});
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [releaseEmail, setReleaseEmail] = useState("");
  const [releaseBusy, setReleaseBusy] = useState(false);

  const load = () => {
    setRows(null);
    setError(null);
    Promise.all([
      api.listDemos(),
      api.listLicenses().catch(() => ({ count: 0, licenses: [] as LicenseRow[] })),
    ])
      .then(([d, l]) => {
        setRows(d.demos);
        const map: Record<string, LicenseRow> = {};
        for (const lic of l.licenses) map[lic.licenseId] = lic;
        setLicMap(map);
      })
      .catch((e: ApiError) => setError(e.status === 403 ? "Brak dostępu (owner-only)." : e.message));
  };
  useEffect(load, []);

  const kpis = useMemo(() => {
    const r = rows ?? [];
    return {
      issued: r.filter((d) => d.partitionKey !== "tenant").length, // domain + free-email issue rows
      activated: r.filter((d) => d.partitionKey === "tenant").length,
    };
  }, [rows]);

  // Requester e-mail joined onto EVERY row (incl. the tenant-activation row) by licenseId — so the
  // tenant row also shows who asked for the demo. Full e-mail exists for new grants; old rows fall
  // back to the masked form.
  const emailByLicense = useMemo(() => {
    const m: Record<string, string> = {};
    for (const d of rows ?? []) if (d.email) m[d.licenseId] = d.email;
    return m;
  }, [rows]);
  const emailOf = (d: DemoGrantRow) => d.email ?? emailByLicense[d.licenseId] ?? d.emailMasked ?? "—";
  const domainFromEmail = (e?: string) => { const at = (e ?? "").indexOf("@"); return at >= 0 ? e!.slice(at + 1) : ""; };
  const domainOrTenant = (d: DemoGrantRow) =>
    d.partitionKey === "tenant" ? d.rowKey : (d.domain || domainFromEmail(emailOf(d)) || "—");
  const typeLabel = (d: DemoGrantRow) =>
    d.partitionKey === "tenant" ? "Tenant" : d.partitionKey === "domain" ? "Domena" : "E-mail";

  const filtered = useMemo(() => {
    const r = rows ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return r;
    return r.filter((d) =>
      (d.domain ?? "").toLowerCase().includes(q) ||
      (d.email ?? "").toLowerCase().includes(q) ||
      (d.emailMasked ?? "").toLowerCase().includes(q) ||
      d.rowKey.toLowerCase().includes(q) ||
      d.licenseId.toLowerCase().includes(q));
  }, [rows, query]);

  const columns: Column<DemoGrantRow>[] = useMemo(() => [
    {
      key: "type", label: "Typ",
      get: (d) => typeLabel(d),
      render: (d) => (
        <span className={`badge st-${d.partitionKey === "tenant" ? "active" : "issued"}`}>{typeLabel(d)}</span>
      ),
    },
    {
      key: "email", label: "E-mail klienta",
      get: (d) => emailOf(d),
      render: (d) => <span style={{ fontSize: 13 }}>{emailOf(d)}</span>,
    },
    {
      key: "domainTenant", label: "Domena / tenant",
      get: (d) => domainOrTenant(d),
      render: (d) => (
        <span className={d.partitionKey === "tenant" ? "mono" : ""} style={{ fontSize: d.partitionKey === "tenant" ? 11 : 13 }}>
          {domainOrTenant(d)}
        </span>
      ),
    },
    { key: "license", label: "Licencja", get: (d) => d.licenseId, render: (d) => <span className="faint mono" style={{ fontSize: 11 }}>{d.licenseId}</span> },
    { key: "granted", label: "Wydano", align: "right", filter: "date", get: (d) => new Date(d.grantedAt).getTime(), render: (d) => <span className="tabular">{fmtDate(d.grantedAt)}</span> },
    { key: "activated", label: "Aktywowano", align: "right", filter: "date", get: (d) => (d.activatedAt ? new Date(d.activatedAt).getTime() : 0), render: (d) => <span className="tabular">{d.activatedAt ? fmtDate(d.activatedAt) : "—"}</span> },
    {
      key: "expires", label: "Ważna do", align: "right", filter: "date",
      get: (d) => { const l = licMap[d.licenseId]; return l ? new Date(l.expiresAt).getTime() : 0; },
      render: (d) => { const l = licMap[d.licenseId]; return <span className="tabular">{l ? fmtDate(l.expiresAt) : "—"}</span>; },
    },
    {
      key: "status", label: "Status",
      get: (d) => { const l = licMap[d.licenseId]; return l ? visualStatus(l).label : "—"; },
      render: (d) => {
        const l = licMap[d.licenseId];
        if (!l) return <span className="faint">—</span>;
        const vs = visualStatus(l);
        return <span className={`badge st-${vs.key}`}><span className={`dot dot-${vs.key}`} />{vs.label}</span>;
      },
    },
    { key: "source", label: "Źródło", get: (d) => d.source ?? "", render: (d) => d.source ?? "—" },
  ], [licMap, emailByLicense]);

  const releaseBlock = async () => {
    const val = releaseEmail.trim();
    if (!EMAIL_OR_DOMAIN_RE.test(val)) { toast("Podaj adres e-mail lub domenę firmową"); return; }
    setReleaseBusy(true);
    try {
      await api.removeDemoEmail(val); // backend accepts an e-mail or a bare domain
      toast("Zdjęto blokadę — ta domena może ponownie poprosić o DEMO w portalu");
      setReleaseEmail("");
      load();
    } catch (e) { toast((e as ApiError).message); } finally { setReleaseBusy(false); }
  };

  return (
    <>
      <div className="kpi-grid" style={{ marginBottom: 22 }}>
        <Kpi label="Wydane DEMO (domena)" value={kpis.issued} accent="accent" />
        <Kpi label="Aktywowane (tenant)" value={kpis.activated} />
      </div>

      <div className="row" style={{ marginBottom: 14 }}>
        <div style={{ position: "relative", flex: 1, maxWidth: 360 }}>
          <span style={{ position: "absolute", left: 11, top: 10, color: "var(--text-faint)" }}><IconSearch size={15} /></span>
          <input className="input" style={{ paddingLeft: 34 }} placeholder="Szukaj: e-mail / tenant / licencja…"
            value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div className="topbar-spacer" />
        <button className="btn" onClick={load}><IconRefresh size={15} /> Odśwież</button>
        <button className="btn primary" onClick={() => setShowModal(true)}><IconPlus size={15} /> Wydaj DEMO</button>
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 14 }}>
        <div className="faint" style={{ fontSize: 12, marginBottom: 8 }}>
          Zwolnij domenę — pozwala klientowi ponownie poprosić o DEMO w portalu (np. gdy poprzednie wygasło).
          Wpisz domenę firmową (np. firma.pl) albo dowolny adres e-mail z tej domeny.
        </div>
        <div className="row">
          <input className="input" style={{ flex: 1, maxWidth: 360 }} placeholder="firma.pl lub adres@firma.pl"
            value={releaseEmail} onChange={(e) => setReleaseEmail(e.target.value)} />
          <button className="btn" disabled={releaseBusy} onClick={releaseBlock}>
            {releaseBusy ? "Zwalniam…" : "Zwolnij domenę"}
          </button>
        </div>
      </div>

      {error ? (
        <div className="card"><div className="empty">{error}</div></div>
      ) : !rows ? (
        <div className="card"><div className="empty">Ładowanie…</div></div>
      ) : (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(d) => `${d.partitionKey}:${d.rowKey}`}
          initialSort={{ key: "granted", dir: "desc" }}
          empty={rows.length === 0 ? "Brak wydanych licencji DEMO. Kliknij „Wydaj DEMO”, aby wystawić pierwszą." : "Brak wyników dla filtrów."}
        />
      )}

      {showModal && <IssueDemoModal onClose={() => setShowModal(false)} onIssued={() => { setShowModal(false); load(); }} />}
    </>
  );

  function Kpi({ label, value, accent }: { label: string; value: number; accent?: string }) {
    const color = accent === "accent" ? "var(--accent-text)" : "var(--text)";
    return (
      <div className="card kpi">
        <div className="kpi-label">{label}</div>
        <div className="kpi-value" style={{ color }}>{value}</div>
      </div>
    );
  }
}

// ---- Issue DEMO modal -------------------------------------------------------

function IssueDemoModal({ onClose, onIssued }: { onClose: () => void; onIssued: () => void }) {
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState<{ domain?: string; licenseId?: string } | null>(null);
  const [result, setResult] = useState<{ issue: DemoIssueResult; link: DownloadLink | null } | null>(null);

  const generate = async (force = false) => {
    if (!EMAIL_RE.test(email.trim())) { toast("Podaj poprawny adres e-mail"); return; }
    setBusy(true);
    try {
      const issue = await api.issueDemo({ email: email.trim(), force });
      if (issue.duplicate) {
        setDup({ domain: issue.domain, licenseId: issue.licenseId });
        return;
      }
      // DEMO uses the BETA build (the stable 1.0.x line has no DEMO enforcement).
      const link = await api.downloadLink("beta").catch(() => null);
      setResult({ issue, link });
    } catch (e) {
      toast((e as ApiError).message || "Nie udało się wygenerować klucza DEMO");
    } finally {
      setBusy(false);
    }
  };

  const emailBlock = result
    ? [
        `Klucz licencyjny DEMO ARVETIO Access Audit for Microsoft 365:`,
        ``,
        `    ${result.issue.key}`,
        ``,
        `Zakres DEMO: pełna funkcjonalność, limit — witryny SharePoint: ${result.issue.maxSites ?? 1}, konta OneDrive: ${result.issue.maxOneDrive ?? 3}.`,
        `Ważny do: ${fmtDate(result.issue.expiresAt)}.`,
        ``,
        result.link ? `Instalator (${result.link.version}): ${result.link.url}` : `Instalator (beta): https://api.arvetio.com/download?channel=beta`,
        result.link ? `SHA-256: ${result.link.sha256}` : ``,
        ``,
        `Po instalacji zaloguj się kontem Microsoft i aktywuj powyższy klucz.`,
        `Licencję DEMO można wydać raz na domenę firmową i raz na tenant Microsoft 365.`,
      ].filter((l) => l !== undefined).join("\n")
    : "";

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ padding: "18px 22px", borderBottom: "1px solid var(--border)" }}>
          <h3 style={{ fontSize: 16 }}>{result ? "Klucz DEMO wygenerowany" : "Wydaj licencję DEMO"}</h3>
          <div className="topbar-spacer" />
          <button className="btn icon sm ghost" onClick={onClose}><IconX size={16} /></button>
        </div>

        <div style={{ padding: 22 }}>
          {!result ? (
            <>
              <p className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
                Klucz DEMO: pełna funkcjonalność, 1 witryna SharePoint i 3 konta OneDrive, ważny 7 dni.
                Jeden klucz na domenę firmową i jeden na tenant Microsoft 365 (dla darmowych skrzynek — jeden na adres).
              </p>
              <div className="field">
                <label className="label">E-mail klienta</label>
                <input className="input" value={email} autoFocus
                  onChange={(e) => { setEmail(e.target.value); setDup(null); }}
                  placeholder="kontakt@firma.pl" />
              </div>
              {dup && (
                <div style={{ marginTop: 6, padding: 12, border: "1px solid var(--medium)", borderRadius: 8, background: "var(--medium-soft, transparent)" }}>
                  <div style={{ fontSize: 12.5, marginBottom: 8 }}>
                    Na domenę <b>{dup.domain}</b> wydano już licencję DEMO
                    {dup.licenseId ? <> — <span className="mono" style={{ fontSize: 11 }}>{dup.licenseId}</span></> : null}.
                    Możesz mimo to wydać kolejny klucz dla tej domeny.
                  </div>
                  <button className="btn danger sm" disabled={busy} onClick={() => generate(true)}>
                    {busy ? "Generuję…" : "Wydaj ponownie mimo to"}
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
                Skopiuj blok i wyślij klientowi. Klucz pokazujemy <b>tylko teraz</b> — backend trzyma jego hash.
              </p>
              <div className="result-block">{emailBlock}</div>
              <button className="btn primary" style={{ marginTop: 14, width: "100%" }}
                onClick={() => { navigator.clipboard.writeText(emailBlock); toast("Skopiowano blok do schowka"); }}>
                <IconCopy size={15} /> Kopiuj blok do maila
              </button>
            </>
          )}
        </div>

        <div className="row" style={{ padding: "16px 22px", borderTop: "1px solid var(--border)", justifyContent: "flex-end" }}>
          {!result ? (
            <>
              <button className="btn ghost" onClick={onClose}>Anuluj</button>
              <button className="btn primary" disabled={busy} onClick={() => generate(false)}>{busy ? "Generuję…" : "Wydaj DEMO"}</button>
            </>
          ) : (
            <button className="btn primary" onClick={onIssued}>Gotowe</button>
          )}
        </div>
      </div>
    </div>
  );
}
